import Stripe from "stripe";
import { z } from "zod";
import {
  EXISTING_AUTH_WITH_APPLE_ERR,
  EXISTING_AUTH_WITH_GOOGLE_ERR,
} from "./shared-util";
/* `export * from "./roles"` re-exports but introduces no local binding,
 * and `src/types.d.ts` also declares a global `Id` type equal to `string`.
 * Without this import, every `id: Id` in this file silently rebinds to
 * that global `string`. Keep both lines. */
import { Id, ROLE, Roles, SPACE_MEMBER_TYPE } from "./roles";

export const AUDIO_ACCEPT_TYPES = [
  "audio/mp3",
  "audio/x-mp3",
  "audio/x-m4a",
  "audio/mpeg",
  "audio/x-mpeg",
  "audio/x-mpeg3",
  "audio/x-mpegaudio",
  "audio/x-mpg",
  "audio/mp4",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/webm",
  "audio/aif",
  "audio/aiff",
  "audio/x-aif",
  "audio/x-aiff",
];

export const AUDIO_ACCEPT_TYPES_LIST = [
  ".mp3",
  ".mp4",
  ".m4a",
  ".wav",
  ".ogg",
  ".aif",
  ".aiff",
  ".webm",
];

export const AUDIO_ACCEPT_TYPES_STRING =
  ".mp3, .mp4, .m4a, .wav, .ogg, .aif, .aiff, or .webm";

/* SHARED
---------------------------------- */
/*
 * The role vocabulary (`PUBLIC_ID`, `SHARE_KEY_PREFIX`, `SHARE_KEY_REGEX`,
 * `Id`, `ROLE`, `COMPATIBLE_ROLES`, `Roles`) moved to `./roles`, a leaf
 * module with no imports of its own. The `migrate/` scripts compile that
 * module on its own into `migrate/.generated/`, and the leaf keeps
 * `stripe`, `zod` and `shared-util` out of that CommonJS build. The
 * re-export below keeps every `import { ROLE } from "…/entities"` site
 * unchanged.
 */
export * from "./roles";
export const DELETE_FIRESTORE_FIELD = "DELETE_FIRESTORE_FIELD";

export type ArrayOperation<T> = ArrayUnion<T> | ArrayRemove<T>;

/**
 * used to add a new item to an array in a firestore document.
 * https://firebase.google.com/docs/firestore/manage-data/add-data#update_elements_in_an_array
 */
export class ArrayUnion<T = void> {
  arrayUnion: T[];
  constructor(arr: T[]) {
    this.arrayUnion = arr;
  }
}

/**
 * used to remove an item from an array in a firestore document, and it doesn't
 * fail even when the item is not found in the array.
 * https://firebase.google.com/docs/firestore/manage-data/add-data#update_elements_in_an_array
 */
export class ArrayRemove<T = void> {
  arrayRemove: T[];
  constructor(arr: T[]) {
    this.arrayRemove = arr;
  }
}

export enum PASSWORD_TYPE {
  Share = "share",
  Public = "public",
}

type EntityCore = {
  id: Id;
  createdBy: Id;
  createdAt: number;
  updatedAt?: number;
  createdByName?: string;
};

export type Entity = EntityCore &
  PasswordProtectableEntity & {
    createdByAlias?: string;
    lastUpdatedBy?: Id;

    // PERMISSIONS-RELATED
    // these fields may be populated for other entities than Spaces,
    // but they are only used for Spaces at the moment.
    // the following is an example of how permission related fields work:
    // - space A
    //   - rolesV2: { userA: [MANAGE] }
    //   - readableByV2: [userA]
    //
    //   - space B (child of space A)
    //     - rolesV2: { userB: [MANAGE] }
    //     - inheritedRolesV3: { userA: [MANAGE] }
    //     - readableByV2: [userA, userB]
    //     - topLevelReadableBy: [userB]
    //
    //     - space C (child of space B)
    //       - rolesV2: { userC: [MANAGE] }
    //       - inheritedRolesV3: { userA: [MANAGE], userB: [MANAGE] }
    //       - readableByV2: [userA, userB, userC]
    //       - topLevelReadableBy: [userC]

    /**
     * rolesV2 field contains information on who has what roles in a given entity
     */
    rolesV2?: Roles;
    /**
     * inheritedRolesV3 field contains information on what roles are inherited
     * from its parents (e.g., parent space)
     */
    inheritedRolesV3?: Roles;
    /*
     * readableByV2 is an array of user IDs who has read access to the entity and is just
     * a list of keys in rolesV2 and inheritedRolesV3 dictionary.
     * this field is used to query for all spaces that a user has read access to
     */
    readableByV2?: Id[];
    /**
     * topLevelReadableBy is an array of user IDs who has access to the entity
     * at the top level.
     * for example, if a user has access to a space, but not to its parent,
     * the space is a top level space to this user, but not to users who have
     * access to the parent space. and we use this field to query for top level
     * spaces for a given user.
     * the value for this field is a list of keys in the entity's rolesV2 field
     * that are not found in the entity's inheritedRolesV3 field.
     */
    topLevelReadableBy?: Id[];

    /** 
      * For each of these users, this entity was affected during an
  account link.
      * We use this to know what to un-merge if they unlink.
      */
    mergedUserIds?: string[];

    /**
     * this should just be a mirror of subscription tier of `createdBy` user
     */
    subscriptionTier?: SUBSCRIPTION_TIER;
  };

export type Auth0User = {
  created_at?: string;
  phone_number?: string;
  email?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  last_login?: string;
  logins_count?: number;
  name?: string;
  nickname?: string;
  picture?: string;
  updated_at?: string;
  user_id?: string;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  app_metadata?: Record<string, any>;
  user_metadata?: Record<string, any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  identities?: {
    connection: string;
    user_id: string;
    provider: string;
    profileData?: {
      email?: string;
      phone_number?: string;
    };
  }[];
};

/* KNOCK EVENTS
---------------------------------- */
// Must match IDs in Knock dashboard to trigger workflows
export enum KNOCK_WORKFLOW {
  DOWNLOAD_LINK_FAILED = "download-link-failed",
  DOWNLOAD_LINK_READY = "download-link-ready",
  TRACK_ADDED = "track-added",
  TRACK_PLAYED = "track-played",
  TRACK_DOWNLOADED = "track-downloaded",
  TRACK_VERSION_ADDED = "track-version-added",
  TRACK_ATTACHMENT_ADDED = "track-attachment-added",
  SPACE_ITEMS_DOWNLOADED = "space-items-downloaded",
  CARD_COMMENT_ADDED = "card-comment-added",
  CARD_REPLY_ADDED = "card-reply-added",
  CARD_RESOLVED = "card-resolved",
  CHAT_COMMENT_ADDED = "chat-comment-added",
  SUBSCRIPTION_UPDATED = "subscription-updated",
  SPACE_MEMBER_ADDED = "space-member-added",
  SPACE_ATTACHMENT_ADDED = "space-attachment-added",
  SPACE_MEMBER_JOINED = "space-member-joined",
  SPACE_MEMBER_REQUESTED = "space-member-requested",
  SPACE_DOWNLOADED = "space-downloaded",
  SPACE_ATTACHMENT_DOWNLOADED = "space-attachment-downloaded",
  TRACK_ATTACHMENT_DOWNLOADED = "track-attachment-downloaded",
  TRACK_VERSION_DOWNLOADED = "track-version-downloaded",
  ITEMS_ADDED_TO_DRAFT_SPACE = "items-added-to-draft-space",
  RECEIVED_TRACK_SUBMISSION = "received-track-submission",
  SUBMITTED_TRACK = "submitted-track",
  SUBMITTED_TRACK_ACCEPTED = "submitted-track-accepted",
}

export type NotificationDataCore = {
  origin?: string;
  artworkUrl?: string;
};

export type DownloadLinkFailedData = NotificationDataCore & {
  downloadRequestId: string;
  entityId: string;
  entityName: string;
  entityType: string;
  createdBy: string;
  subscribers: string[];
};

export type DownloadLinkReadyData = DownloadLinkFailedData;

export type SpaceMemberAddedData = NotificationDataCore &
  SpaceMemberJoinedData & {
    spaces: Array<{
      id: string;
      name: string;
      artworkUrl: string;
    }>;
  };

export type SpaceMemberRequestedData = NotificationDataCore & {
  displayName: string;
  identifier?: string;
  spaceId: string;
  spaceName?: string;
};

export type SpaceMemberJoinedData = NotificationDataCore & {
  memberId: string;
  spaceId: string;
  spaceName: string;
  /**
   * Optional to match `SpaceCore.description?: string`, which is the only
   * source: `onRolesUpdated.ts` passes `entity.description` straight
   * through. The field was required before, and that made the payload's
   * type wrong for every space with no description.
   */
  spaceDescription?: string;
  tracks: { trackId: string; trackName: string; trackArtworkUrl: string }[];
};

export type CardCommentAddedData = NotificationDataCore & {
  spaceId: string;
  spaceName: string;

  trackId: string;
  trackName: string;

  versionId: string;
  versionName: string;

  commentId: string;
  commentType: string;
  commentText: string;
  commentTimestamp: string;
};

export type CardReplyAddedData = CardCommentAddedData & {
  rootCommentId: string;
  rootCommentType: string;
  rootCommentText: string;
  rootCommentTimestamp: string;
};

export type CardResolvedData = CardCommentAddedData;

export type ChatCommentAddedData = NotificationDataCore & {
  spaceId: Id;
  spaceName: string;

  commentId: Id;
  commentText: string;
  commentDatetime: string;
};

export type SubscriptionUpdateData = NotificationDataCore & {
  userId: string;
  updateTitle: string;
  updateDescription: string;
};

type baseTrackData = {
  spaceId: string;
  spaceName: string;

  trackId: string;
  trackName: string;
  trackArtworkUrl: string;
};

export type TrackAddedData = NotificationDataCore &
  baseTrackData & {
    commentCount?: number;
  };

export type TrackDownloadedData = TrackAddedData;

export type TrackAttachmentAddedData = NotificationDataCore & {
  fileId: string;
  fileName: string;
  spaceId: string;
  trackArtworkUrl: string;
  trackId: string;
  trackName: string;
};

export type TrackAttachmentsDownloadedData = NotificationDataCore &
  baseTrackData & {
    files: Array<{
      fileId: string;
      fileName: string;
    }>;
  };

export type TrackAttachmentDownloadedData = NotificationDataCore &
  baseTrackData & {
    fileId: string;
    fileName: string;
  };

export type TrackVersionAddedData = NotificationDataCore &
  baseTrackData & {
    versionId: string;
  };

export type TrackPlayedData = NotificationDataCore & {
  spaceId: string;
  spaceName: string;

  trackArtworkUrl: string;
  trackId: string;
  trackName: string;

  listenerId: string;
  numPlays: number;
  isInboxSpace?: boolean;
};

export type TrackVersionDownloadedData = NotificationDataCore &
  baseTrackData & {
    fileId: string;
    fileName: string;
    versionNumber: number;
  };

export type TrackVersionsDownloadedData = NotificationDataCore &
  baseTrackData & {
    files: Array<{
      fileId: string;
      fileName: string;
      versionNumber: number;
    }>;
  };

export type SpaceAttachmentAddedData = NotificationDataCore & {
  fileId: string;
  fileName: string;
  spaceId: string;
  spaceName: string;
};

export type SpaceDownloadedData = NotificationDataCore & {
  spaceId: string;
  spaceName: string;
};

export type SpaceAttachmentDownloadedData = SpaceAttachmentAddedData;

// eslint-disable-next-line @typescript-eslint/ban-types
export type SpaceItemsDownloadedData = NotificationDataCore & {};

export type ItemsAddedToDraftSpaceData = NotificationDataCore & {
  spaceId: string;
  deletedSpaceName: string;
  deleterName: string;
  entityType: COLLECTION_ID;
  entityName: string;
};

export type NotificationData =
  | TrackVersionAddedData
  | TrackVersionDownloadedData
  | TrackVersionsDownloadedData
  | TrackAddedData
  | TrackDownloadedData
  | TrackAttachmentAddedData
  | TrackAttachmentDownloadedData
  | TrackAttachmentsDownloadedData
  | SpaceMemberAddedData
  | CardReplyAddedData
  | CardResolvedData
  | SpaceMemberJoinedData
  | SpaceMemberRequestedData
  | CardCommentAddedData
  | ChatCommentAddedData
  | SubscriptionUpdateData
  | SpaceItemsDownloadedData
  | SpaceAttachmentAddedData
  | SpaceAttachmentDownloadedData
  | SpaceDownloadedData
  | ItemsAddedToDraftSpaceData
  | FormSubmissionData
  | TrackPlayedData;

export type FormSubmissionData = NotificationDataCore & {
  spaceId: string;
  spaceName: string;
  spaceCreatedBy: string;
  spaceCreatedByName: string;
  trackId: string;
  trackName: string;
  trackDescription: string;
  artistName: string;
  submitterFirstName?: string;
  submitterLastName?: string;
  submitterMonthlyListenersGTE?: number;
};

// Must match IDs in Knock dashboard (see Preferences)
export enum CHANNEL {
  FEED = "in_app_feed",
  EMAIL = "email",
  SMS = "sms",
  PUSH = "push",
}

export type DesktopSyncCore = {
  /**
   * this is used to indentify if a document was updated or created in a specific sync cycle.
   * Used by the desktop client to determine if a file update is a result of it's own generated action.
   */
  syncId?: string;
};

export enum FILE_PARENT_REFERENCE_TYPE {
  SPACE_ATTACHMENT = "space-attachment",
  TRACK_ATTACHMENT = "track-attachment",
  TRACK_VERSION = "track-version",
}

/* FILES
---------------------------------- */
export type FileCore = DesktopSyncCore & {
  /**
   * The space whose access governs this file. This is not a parent pointer
   * in the `SpaceCore.spaceId` sense: code reaches a file through
   * `parentEntityId` plus `parentReferenceType`, and to resolve that chain
   * to a space costs an extra read for every track-parented file. This
   * field stores the answer directly, so no extra read is necessary.
   *
   * The writer derives it where the code already maintains `readableByV2`:
   * the parent's own id when `parentReferenceType` is SPACE_ATTACHMENT,
   * otherwise the parent's `spaceId`.
   *
   * The field is optional, and nothing writes it yet. The writer and the
   * one-off backfill arrive in a later change, so nothing can assume that
   * the field is present. Any caller that needs the file's space must keep
   * its `parentEntityId` / `versionTracks` fallback, and any rule that
   * reads it must keep the legacy `readableByV2` check alongside.
   *
   * Single-valued by design. For a legacy `versionTracks` file reachable
   * from more than one space, the field records only the `parentEntityId`
   * space; `readableByV2` alone covers the other space.
   */
  spaceId?: Id;
  name: string;
  url?: string;
  storagePath?: string;
  fileType?: string;
  fileName?: string;
  size?: number;
  /**
   * metadata field is only populated only if it's an audio file and after
   * the processing is done. it contains information such as waveform, duration,
   * codec_name, and bit_rate. see @highnote/server/src/core/storage/processFile.ts
   * for more information
   */
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  uploadedBy?: string;

  isProcessing?: boolean;
  isProcessedV3?: boolean;
  /*
   * this field is only relevant for audio files that are processed
   * as track versions and contains information on any errors that happened
   * while processing the audio file.
   */
  processingErrorV3?: string;

  /*
   * indicates whether the audio file is currently being transcribed
   */
  isTranscribing?: boolean;
  /*
   * indicates whether transcription processing has completed (successfully or with error)
   */
  isTranscribed?: boolean;
  /*
   * contains information on any errors that happened while transcribing the audio file
   */
  transcriptionError?: string;
  /*
   * indicates whether AI comments are currently being generated
   */
  isGeneratingAIComments?: boolean;
  /*
   * indicates whether AI comment generation has completed (successfully or with error)
   */
  aiCommentsGenerated?: boolean;
  /*
   * contains information on any errors that happened while generating AI comments
   */
  aiCommentGenerationError?: string;

  // TODO: deprecate versionTracks and versionTracksCount in favor of
  // parentEntityId and parentReferenceType fields
  // https://www.notion.so/highnotefm/Deprecate-versionTracks-and-versionTracks-count-for-files-v1-1df15dd148288028b164db6edbee8023
  /*
   * this field is used to store IDs of tracks where this file is being used as
   * versions as an audio file can be used in multiple tracks.
   * (e.g., duplicated tracks and tracks in starter template space)
   */
  versionTracks?: string[];
  /*
   * this simply stores the length of `versionTracks` array and is used when
   * querying files that are being used as track versions since you can't query
   * for documents by array field's length.
   */
  versionTracksCount?: number;
  /*
   * parentEntityId indicates the ID of the entity (e.g., space or track) that
   * this file is attached to.
   */
  parentEntityId?: string;
  /*
   * parentReferenceType indicates the relationship between the file entity and
   * its parent entity.
   */
  parentReferenceType?: FILE_PARENT_REFERENCE_TYPE;
  /**
   * Go-forward tombstone (track-sharing plan §4.6, Task 9 post-review fix),
   * written ONLY by `disassociateFilesFromParent`
   * (`core/db/files.ts:850-879`) at the moment a file is detached from its
   * track/space. Never cleared once set. Exists because detachment clears
   * `parentEntityId`, `parentReferenceType`, AND `versionTracks`/
   * `versionTracksCount` together in the same write — so a former
   * track-version file becomes indistinguishable, on its OWN fields, from a
   * brand-new comment/poll attachment that never had a parent at all. READ
   * BY `core/db/fileAuthorization.ts`'s `authorizeFileAccess`: a
   * `wasParented: true` file is routed to the restricted
   * `readableByV2`/`createdBy` fallback instead of the STRICT no-parent
   * class's any-authenticated grant. Server-written; not client-settable.
   * Go-forward only — a file detached BEFORE this field existed has no
   * tombstone and is accepted as a residual, explicitly documented risk
   * (see `authorizeFileAccess`'s CONTROLLER RISK ACCEPTANCE comment).
   */
  wasParented?: boolean;

  /**
   * A propagated mirror of this file's parent revision: the space's
   * `membersRev`, or, for a track-parented file, the track's `aclRev`. The
   * writer copies the value and never increments it. An increment is not
   * idempotent (one delivery and a repeat delivery give different results)
   * when the task system re-delivers a task. Also, nothing can compare an
   * incremented value against its source for equality.
   *
   * Its only job is to change this document, so that open file listeners
   * re-run the security rules. Firestore re-runs a listener's rules when
   * the listened document changes, not when a document those rules `get()`
   * changes. The write itself is the mechanism; no rule and no client
   * reads the value.
   *
   * The field is absent until the first revocation touches the document;
   * that is safe.
   *
   * The field is exempt from indexes (`"indexes": []`). It changes on
   * every revocation, so an auto-index rewrites one index entry for each
   * content document on the exact write path this work exists to make
   * cheap.
   *
   * It does nothing for the media bytes: it re-runs rules, and rules do
   * not govern the bytes. They never did.
   */
  aclRev?: number;
};

export type FileEntity = Entity & FileCore;

export type FileReferences = {
  [COLLECTION_ID.DROPBOX_JOBS]: Track[];
  [COLLECTION_ID.TRACK]: Track[];
  [COLLECTION_ID.SPACE]: Space[];
  [COLLECTION_ID.COMMENT]: Comment[];
};

export enum AUDIO_QUALITY {
  LOW = "low",
  HIGH = "high",
  ORIGINAL = "original",
}

export const AUDIO_QUALITY_LEVEL = {
  [AUDIO_QUALITY.LOW]: 1,
  [AUDIO_QUALITY.HIGH]: 2,
  [AUDIO_QUALITY.ORIGINAL]: 3,
};

export type AudioQualityOption = {
  quality: AUDIO_QUALITY;
  format: string;
  filename: string;
  bitRate?: number;
  samplingRate?: number;
};

export const audioQualityOptions: AudioQualityOption[] = [
  {
    quality: AUDIO_QUALITY.ORIGINAL,
    format: "flac",
    filename: `${AUDIO_QUALITY.ORIGINAL}.flac`,
  },
  {
    quality: AUDIO_QUALITY.ORIGINAL,
    format: "mp3",
    filename: `${AUDIO_QUALITY.ORIGINAL}.mp3`,
  },
  {
    quality: AUDIO_QUALITY.HIGH,
    format: "mp3",
    filename: `${AUDIO_QUALITY.HIGH}.mp3`,
    bitRate: 320000,
    samplingRate: 44100,
  },
  {
    quality: AUDIO_QUALITY.LOW,
    format: "mp3",
    filename: `${AUDIO_QUALITY.LOW}.mp3`,
    bitRate: 96000,
    samplingRate: 44100,
  },
];

type PasswordProtectableEntity = {
  // PASSWORD PROTECTION RELATED - only affects Spaces for now
  sharePassword?: string;
  sharePasswordEnabled?: boolean;
  publicPassword?: string;
  publicPasswordEnabled?: boolean;
};

// Dropbox integrations
export enum JOB_STATUS {
  PENDING = "pending",
  IN_PROGRESS = "inProgress",
  COMPLETED = "completed",
  FAILED = "failed",
}
export enum DROPBOX_JOB_TYPE {
  // download from Dropbox
  DOWNLOAD = "download",

  // upload to Dropbox
  // UPLOAD = "upload",
}

export type DropboxSyncJob = EntityCore & {
  jobType: DROPBOX_JOB_TYPE;
  // this is to reference a file document in COLLECTION_ID.FILE
  fileEntityId: string;
  // this is a file ID from dropbox
  fileId: string;
  // this is a file name from dropbox at the time of download
  fileName: string;
  spaceId: string;
  progress: number;
  processingError?: string;
  status: JOB_STATUS;
};

/* SPACES
---------------------------------- */

export type SpaceVisibility = "visible" | "unlisted";

export type SpaceCore = PasswordProtectableEntity & {
  /**
   * The active share-link key of this space, or `null` when the space has
   * none.
   *
   * The share link is the one product surface that reads a share key on the
   * render path: the Share dialog, `SpaceNavigation` and `SpaceHomeV2` all
   * build a URL from it. Until now the only source was a scan of the
   * `rolesV2` map for a `share_` prefix (`getEntityShareKeys`). That map is
   * the field this migration exists to delete, and after the deletion the
   * scan returns an empty list for every space. The alternative source, a
   * query of the `members-v1` subcollection for the `share` document, costs
   * one query for each space on a path that renders a list of spaces.
   *
   * So the key gets a field of its own. One field, on the document the
   * caller already holds, with no extra read.
   *
   * The server derives it. It is deliberately absent from
   * `SPACE_WRITABLE_FIELDS` (`./db/spaces.ts`), like `inheritanceSyncedAt`:
   * a caller that can set it can hand itself a share link. The writer is
   * the roles branch of `onWriteHooks/space/utils.ts`, which already
   * computes the value for its own use and already writes the other derived
   * fields in the same call. `mintSpaceShareKey` and `updateSpace` need no
   * change, because both write `rolesV2` and the trigger follows.
   *
   * `undefined` and `null` are different. `undefined` means "no writer has
   * run for this space yet", and a reader must fall back to
   * `getEntityShareKeys`. `null` means "the writer ran, and this space has
   * no share key". Every space carries `undefined` until the trigger fires
   * or the one-off backfill runs, so **keep the fallback at every call site
   * until that backfill is complete**.
   *
   * Single-valued on purpose. `getEntityShareKeys` returns a list, and the
   * Share dialog treats every entry after the first as stale and deletes
   * it. This field records the first, which is the one the product uses.
   */
  shareKey?: string | null;
  /*
   * parent space ID
   */
  spaceId?: Id;
  name: string;
  description?: string;
  artworkFile?: Id;
  artworkUrl?: string;
  files?: Id[];
  /**
   * Direct members of this space — the keys of `rolesV2` with share-link
   * tokens stripped, plus `createdBy`. Excludes inherited members from
   * ancestor spaces (use `readableByV2` for that).
   *
   * Exists as its own field because Firestore can't index map keys, so
   * answering "which spaces did this user directly join?" requires a
   * separate `array-contains`-queryable array. Backs the joined-spaces
   * query in `getUserProfile`.
   *
   * Derived via `computeSpaceMemberIds` and kept in sync by `onCreate`,
   * `onSpaceUpdated`, and `updateSpace`. For display, prefer
   * `memberCount` to avoid hydrating the full array.
   */
  memberIds?: string[];
  /**
   * Cached `memberIds.length`. Maintained alongside `memberIds` so the UI
   * can render member counts without hydrating the full id array.
   */
  memberCount?: number;

  /**
   * Space Countdown / Waitlist launch moment, as epoch **milliseconds**
   * (matches the `createdAt` convention — NOT ISO-8601, NOT seconds).
   *
   * A guest-mode ("join to view") space carrying a *future* `countdownFrom`
   * is presented by clients as "waitlisted": a non-member sees a countdown
   * timer in the pre-join preview. There is intentionally NO separate
   * `countdownEnabled` flag — the presence of a future timestamp is the
   * entire signal, and the client decides "waitlisted" as
   * `countdownFrom > now`. This is store-and-echo only: the backend never
   * auto-clears it and runs no scheduled job. Null/absent ⇒ not waitlisted.
   * Set (epoch-ms) or cleared (to null) by a space creator/manager from web
   * space settings via `updateSpace`; mobile never writes it.
   */
  countdownFrom?: number | null;

  isArchived?: boolean;

  /*
   * @deprecated
   * this should be deprecated in favor of itemsOrder
   */
  tracksOrder?: Id[];
  /*
   * indicates order in which space items (e.g., files, tracks, child spaces)
   * show up in the space home page
   */
  itemsOrder?: Id[];

  /*
   * keeps track of the last time a file for a space was updated at
   * (e.g., artwork and attachments, etc.), so that we know when to generate
   * a new download archive for the space
   */
  fileUpdatedAt?: number;

  /*
   * indicates whether the space owner has locked the audio quality for
   * the space, and this field is ignored if `subscriptionTier` is not STUDIO
   */
  audioQualityLocked?: boolean;
  /*
   * this should be identical to space owner's subscriptionTier
   */
  subscriptionTier?: SUBSCRIPTION_TIER;

  /*
   * indicates whether a space is a draft space or not
   * A draft space is where all loose items end up (e.g., tracks uploaded to the library)
   */
  isDraftSpace?: boolean;

  spaceColor?: string;
  /*
   * indicates whether the space chat feature is hidden or not
   */
  chatEnabled?: boolean;
  /*
   * indicates whether this space is the primary space for a submission workflow
   */
  isPrimarySubmissionSpace?: boolean;
  /*
   * indicates whether this space is an auto-created inbox for track submissions
   */
  isSubmissionsInboxSpace?: boolean;
  /*
   * the ID of the corresponding typeformConfig document for this submission space
   */
  typeformConfigId?: string;
  /*
   * indicates whether the space owner has chosen a template for the space
   */
  spaceSetup?: boolean;

  /*
   * experimental feature flag based on owner's experimentalPrivateInboxEnabled
   * - turning this ON makes `privateInboxEnabled` toggleable by space managers
   * - turning this OFF makes `privateInboxEnabled` NOT toggleable and remain OFF
   * - however, this field itself should NOT be modified directly. instead, it should
   *   be synced with space owner's `experimentalPrivateInboxEnabled` field
   */
  experimentalPrivateInboxEnabled?: boolean;
  /*
   * indicates whether private inbox is enabled for the space, and this field
   * is ignored if `experimentalPrivateInboxEnabled` is false
   */
  privateInboxEnabled?: boolean;
  /*
   * URL to which the submitter is redirected after submitting tracks to
   * a private inbox-enabled space
   */
  privateInboxRedirectUrl?: string;
  /*
   * indicates whether the space is visible in public search
   */
  visibility?: SpaceVisibility;

  /**
   * Audit field on spaces created by the standalone Space Generator script
   * (@highnote/server/scripts/spaceGenerator/generateSpace.js). Holds the
   * UID of the real admin user who ran the generator. `space.createdBy`
   * is set to the synthetic demo-creator user, so this field records
   * *who* actually triggered the generation.
   */
  demoSpaceGeneratedBy?: Id;

  /**
   * Boolean flag identifying spaces created by the admin Space Generator
   * wizard. Used for filtering (list/exclude demo spaces in analytics,
   * cleanup scripts, support tooling) — `demoSpaceGeneratedBy` alone can't
   * reliably answer "is this a demo space," so this is a dedicated
   * primitive.
   */
  isGeneratedDemoSpace?: boolean;

  /**
   * The writer increases this value on every write that revokes access
   * for at least one identity. Its only job is to change the space
   * document, so that open space-doc listeners re-run the security rules
   * and drop the revoked identity. It is not a version counter, and
   * nothing compares two values of it except `aclRev`.
   *
   * An absent value means 0. The writer folds the increase into the
   * `updateEntity` call that `handleSpacePermissions` already makes. A
   * separate write doubles the space doc's write rate against the soft
   * limit of about 1 write per second per document, and a member-doc
   * trigger that writes the space doc is forbidden outright.
   *
   * WARNING: Add this field to `sanitizeSpaceData`'s allow-list
   * (`db/spaces.ts`) in the same commit that first writes it. That
   * function drops unknown keys silently, with no error. Without the
   * allow-list entry the write appears to succeed but does nothing, and
   * the revocation fails open (the revoked identity keeps access).
   */
  membersRev?: number;
  /**
   * A mirror of whether the public member document grants read access, not
   * of whether that document exists:
   * `isPublic === (members-v1/public exists && .canRead === true)`. A
   * mirror based on existence alone makes a `public` key that holds `[]`
   * or `[GUESTS_ALLOWED]` world-readable across spaces, tracks, files,
   * comments and download requests, and an additive union rule cannot
   * narrow that back.
   *
   * When the field is absent, rules and clients must read it as `false`.
   * The mirror fails closed: no value means no access.
   *
   * Name hazard: the client's `isPublicView` deliberately checks
   * existence. The two names are one token apart, and their meanings are
   * opposite.
   */
  isPublic?: boolean;
  /**
   * A bounded mirror (at most 9 entries) of
   * `members-v1/public.effectiveRoles`. It exists so that no rule and no
   * off-page call site pays a `get(members-v1/public)` read. It is the
   * only answer that web call sites have when they ask permission
   * questions about spaces other than the open space.
   *
   * The rules must never read it. Today's rules do not check
   * `role_GUESTS_ALLOWED`, and they must not start.
   */
  publicRoles?: ROLE[];
};

export type Space = Entity & SpaceCore;

/* SPACE MEMBERSHIP
---------------------------------- */

/**
 * One membership relation: `spaces-v1/{spaceId}/members-v1/{memberId}`.
 *
 * The document id is the identity key, byte for byte. It is whatever
 * appeared as a key of `space.rolesV2`, or as `space.createdBy`, with no
 * change at all: never lower-cased, never passed through
 * `sanitizeShareKey`, never un-escaped from the invite form. Security
 * rules build the path from the raw `request.auth.uid`, and rules cannot
 * call `lower()`. So any normalization here is a permanent, silent
 * PERMISSION_DENIED.
 *
 * This is not an `Entity`. It carries no `createdBy`, `createdAt` or
 * `rolesV2`, no code writes it through `createEntity` or `updateEntity`,
 * and the two-segment wildcard triggers never touch it.
 *
 * Only the server writes it: `firestore.rules` says
 * `allow write: if false`.
 */
export type SpaceMember = {
  /**
   * A duplicate of the document id. It is present because code all over
   * the codebase reads `entity.id` by habit (and `key={row.id}` in React
   * lists). It is exempt from indexes; nothing queries it.
   */
  id: Id;
  /**
   * The id of the space this membership belongs to, duplicated from the
   * document path.
   *
   * The field is mandatory, not a convenience. The rules block must use
   * `match /{path=**}/members-v1/{memberId}`, or Firestore does not
   * authorise collection-group queries (queries across every `members-v1`
   * subcollection) at all. A recursive `{path=**}` captures the whole
   * prefix (`spaces-v1/<id>`), not the id. Rules therefore read
   * `resource.data.spaceId`.
   *
   * Invariant (a condition that must always hold): `spaceId` must equal
   * the parent document's id. A member doc that claims a `spaceId` it does
   * not live under authorises reads of the claimed space. The writer
   * enforces this, and the reconcile script
   * (`migrate/spaceMembership/3-reconcile.js`) asserts it.
   *
   * This is not the parent space; that is `parentSpaceId`. (On the space
   * doc, `space.spaceId` means "my parent". On a member doc, `spaceId`
   * means "the space I am a membership of".)
   */
  spaceId: Id;
  /**
   * A duplicate of the document id, stored so that queries can filter on
   * it. Firestore cannot usefully filter a collection-group query on
   * `__name__`, and the my-memberships stream is
   * `where('memberId','==',uid)`.
   */
  memberId: Id;
  /**
   * The writer derives this from `memberId` with `classifyIdentityKey`
   * (`core/membership.ts`).
   */
  type: SPACE_MEMBER_TYPE;

  /* --- inputs to the role computation --- */

  /**
   * The roles this space grants directly: `space.rolesV2[memberId]`,
   * exactly. It is `[]` when `hasDirectGrant` is false, and also `[]` when
   * the grant is an explicit "no access" block. `hasDirectGrant` tells the
   * two apart.
   */
  directRoles: ROLE[];
  /**
   * Whether `space.rolesV2[memberId]` was a truthy value, that is, whether
   * the legacy `if (userRoles)` gate in `getFullRoles` passed.
   *
   *  - `[]` is truthy. It replaces the inherited grant, so the effective
   *    roles are `[]` and the member is blocked.
   *  - `null` or absent is falsy. The gate skips it, and the inherited
   *    grant passes through.
   *
   * Both cases store `directRoles: []`, so without this boolean nothing
   * can tell the two apart. The boolean is also what makes the document
   * self-sufficient: the job that copies role changes down to child spaces
   * recomputes `effectiveRoles` from the stored document plus a new
   * `inheritedRoles`, with no re-read of `space.rolesV2`.
   */
  hasDirectGrant: boolean;
  /**
   * `Boolean(space.createdBy) && memberId === space.createdBy`.
   *
   * Required for the same reason as `hasDirectGrant`: without it the
   * document is not self-sufficient for recomputation. The creator
   * override in `computeEffectiveRoles` (`core/membership.ts`) runs last
   * and discards everything the earlier steps produced. So a recomputation
   * from `{directRoles, hasDirectGrant, inheritedRoles}` alone returns the
   * inherited set for the creator, and that strips `[ADMIN]` from the
   * owner of every space whose parent's roles changed.
   *
   * Invariant: at most one document per space has `isCreator: true`, and
   * that document always has `effectiveRoles: [ADMIN]` and
   * `canRead: true`.
   */
  isCreator: boolean;
  /**
   * The roles that flow down from ancestor spaces:
   * `space.inheritedRolesV3[memberId]`, exactly, and before the
   * inherited-admin downgrade.
   *
   * Invariant: this equals the parent space's member-doc `effectiveRoles`
   * for the same `memberId`. That is exactly what `getInheritedRolesV3`
   * writes out: `getFullRoles(parentSpace)`. The computation of
   * `effectiveRoles` applies the inherited-admin downgrade once per level.
   *
   * The writer stores a legacy `null` value as `[]`.
   */
  inheritedRoles: ROLE[];

  /* --- outputs of the role computation --- */

  /**
   * The answer. Read this; never compute permissions from `directRoles`.
   * `computeEffectiveRoles` (`core/membership.ts`) produces it.
   */
  effectiveRoles: ROLE[];
  /**
   * True when `effectiveRoles`, merged with the space's public roles,
   * contains at least one role in `COMPATIBLE_ROLES[ROLE.VIEW]`.
   *
   * An exact mirror of "`memberId` is in `space.readableByV2`". It
   * includes the public merge because `hasRole` merges `roles['public']`
   * into every identity's role set. So on a public space a member blocked
   * with `[]` can read today, and this field reproduces that. Consequence:
   * when the `members-v1/public` doc changes whether it implies VIEW, the
   * writer must rewrite every member doc in the space.
   *
   * `role_GUESTS_ALLOWED` alone does not imply read access.
   */
  canRead: boolean;
  /**
   * `canRead && inheritedRoles.length === 0`: this space is a root in this
   * identity's sidebar. An exact mirror of "`memberId` is in
   * `space.topLevelReadableBy`".
   *
   * Invariant: `isTopLevel === true` implies `canRead === true`. Never
   * compute it on its own. If the two can disagree, the equivalent of
   * today's `isTopLevelReadableByMember || isReadableByMember` check
   * becomes an escalation: a member gains access that nobody granted.
   */
  isTopLevel: boolean;
  /**
   * Whether this identity has its own entry in the space's direct grants
   * (the `rolesV2` key existed, with any value, `null` and `[]` included),
   * or is the space creator.
   *
   * `memberIds` covers direct grants plus the creator only; it never reads
   * `inheritedRolesV3`. A blocked member (`directRoles: []`) is in
   * `memberIds` today, and from the array alone nobody can tell it apart
   * from "no direct entry".
   *
   * Three consumers read it:
   *  - `memberCount`.
   *  - The joined-spaces query in `getUserProfile`. It must carry
   *    `type == 'user' && isDirectMember == true`, or a user's public
   *    profile gains the entire child tree of every space they belong to.
   *  - Mobile's `SpaceMember.isInherited`, which is
   *    `!isDirectMember && inheritedRoles.isNotEmpty`.
   */
  isDirectMember: boolean;
  /**
   * An exact per-key mirror of `computeSpaceMemberIds`; the writer stores
   * `isCountedInMemberIds(facts)`. The value is stored, not derived at
   * read time, for two reasons: `memberCount` stays one `count()`
   * aggregation, and a future fifth `SPACE_MEMBER_TYPE` cannot silently
   * enter a user-visible number.
   */
  inMemberIds: boolean;

  /* --- audit --- */

  /**
   * The creation time of this membership. A backfilled doc gets
   * `space.createdAt`.
   */
  addedAt: number;
  /**
   * Who granted the membership. Absent on backfilled docs: the history
   * does not exist, and `space.lastUpdatedBy` does not record it. Exempt
   * from indexes; nothing queries it.
   */
  addedBy?: Id;
  /**
   * The time of the last write to this document. It is not
   * `space.updatedAt`.
   */
  updatedAt: number;
  /**
   * The `space.updatedAt` this document derives from. The dual-writer's
   * no-op guard uses it. Exempt from indexes. It does not prove that any
   * process completed.
   */
  sourceUpdatedAt?: number;

  /* --- invite-claim audit --- */

  /**
   * The `invite:<email>` doc that the server promoted into this
   * membership.
   */
  claimedFrom?: Id;
  /** The time of that promotion. */
  claimedAt?: number;
  /**
   * How the claimant proved ownership of the invited email address, for
   * example `"auth0:email_verified"`. `convertAnyInvites` sets it. Absent
   * means "not claimed via an invite".
   */
  claimVerification?: string;

  /* --- display copies: `type === SPACE_MEMBER_TYPE.USER` only --- */

  /**
   * `space.name`, the sidebar's `orderBy` key. When any field in this
   * block changes, the writer writes every member doc in the space once.
   * The set of fields is deliberately minimal for that reason.
   */
  spaceName?: string;
  /** `space.artworkFile`. */
  spaceArtworkFile?: Id;
  /** `space.isArchived`. This is the field that changes most often. */
  isArchived?: boolean;
  /**
   * `space.spaceId`, the space's parent. It costs nothing extra on the
   * reparent path, because a reparent already rewrites `inheritedRoles`,
   * `effectiveRoles` and `isTopLevel` on every member doc.
   */
  parentSpaceId?: Id | null;
  /**
   * `space.createdBy`. In practice it never changes, so it adds no member
   * writes.
   */
  spaceCreatedBy?: Id;

  /* --- end of the display copies --- */

  /**
   * `space.createdAt`. Required on every member document type (`user`,
   * `public`, `share`, `invite`) and on `canRead: false` documents.
   *
   * This field is the generation guard for the member purge that runs on
   * space delete. Space ids can come from the client, and a future space
   * can reuse one, so the guard ties each member document to one
   * generation of the space. The purge deletes a member document only when
   * `(stored.spaceCreatedAt ?? 0) === space.createdAt`, and it counts a
   * mismatch as `skippedNewerGeneration`. If the field is absent,
   * `undefined ?? 0` is `0`, `0 !== space.createdAt` for every real space,
   * and the purge skips the document. That leaves a world-readable
   * `public` grant behind, on a space id that a client supplied and that a
   * future space can reuse.
   *
   * Do not move it into the display-copies block above.
   */
  spaceCreatedAt: number;
};

export type DownloadRequestCore = {
  entityId: Id;
  entityType: ARCHIVABLE_ENTITY_TYPES;
  isProcessing?: boolean;
  processingError?: string;
  storagePath?: string;
  /*
   * list of user IDs who have requested the download and should be notified
   * when the download is ready
   */
  subscribers?: string[];
  /*
   * dictionary of user IDs and the timestamp after which the user should NOT
   * be notified.
   */
  subscriptions?: Record<string, number>;
};

export type DownloadRequest = Entity & DownloadRequestCore;

/* TRACKS
---------------------------------- */

/** Voice-note payload of a featured comment, for the tile's static preview. */
export type TrackFeaturedCommentVoice = {
  /** Voice-note duration in milliseconds. */
  durationMs: number;
  /** Downsampled, normalized (0..1) amplitude points (≤40). May be empty. */
  peaks: number[];
};

/**
 * Denormalized summary of a track's featured comment, maintained by the
 * trackCommentSummary Cloud Function trigger. The body is denormalized inline
 * (text or voice) so the space-page tile renders with no per-tile read.
 */
export type TrackFeaturedComment = {
  /** The featured comment's document id in comments-v1. */
  commentId: Id;
  /** Whether this is a text or voice comment. */
  contentType: "text" | "voice";
  /** Inline body: text comments → the text; voice → the caption (may be ""). */
  text: string;
  /** The comment's createdBy user id. */
  authorId: Id;
  /** Author display name snapshotted at computation time. */
  authorName: string;
  /** Author profile picture URL, or null when none. */
  authorAvatarUrl?: string | null;
  /** The comment's createdAt, epoch milliseconds. */
  createdAt: number;
  /** Voice payload, present only when contentType === "voice". */
  voice?: TrackFeaturedCommentVoice | null;
};

/** One entry in a track's recent-commenter avatar stack. */
export type TrackCommenterAvatar = {
  /** The commenter's user id (de-dup key + avatar color seed on the client). */
  authorId: Id;
  /** Author display name snapshotted at computation time. */
  authorName: string;
  /** Author profile picture URL, or null when none. */
  authorAvatarUrl?: string | null;
};

export type TrackCore = DesktopSyncCore & {
  /*
   * parent space ID
   */
  spaceId?: Id;
  title: string;
  description?: string;
  artistName?: string;

  files?: Id[];
  artworkFile?: Id;

  defaultVersionId?: Id;
  versionFilesV2: Id[];
  pinnedVersionFiles?: Id[];
  // TODO: get rid of this?
  path?: string;

  /*
   * keeps track of the last time a file for a track was updated at
   * (e.g., artwork and attachments, etc.), so that we know when to generate
   * a new download archive for the track
   */
  fileUpdatedAt?: number;

  /**
   * Summary of the track's featured comment, or null when no comment is
   * eligible OR once the track is shared (see `recentCommenters` below).
   * Denormalized by the trackCommentSummary trigger.
   */
  featuredComment?: TrackFeaturedComment | null;

  /**
   * Up-to-3 most-recent unique commenters, most-recent first. Every comment
   * type counts equally. Denormalized by the
   * trackCommentSummary trigger. `null` once the track is shared (it has
   * more than one space-tracks-v1 row) — plan §4.4: the per-space summary
   * lives on the row, not here, once a track is shared.
   */
  recentCommenters?: TrackCommenterAvatar[] | null;

  /**
   * Total distinct commenters on the track, counting every comment type
   * (reactions included). The client renders "+x" where
   * x = uniqueCommenterCount - recentCommenters.length. `null` once the
   * track is shared — see `recentCommenters` above.
   */
  uniqueCommenterCount?: number | null;

  /** The track creator's profile picture URL, snapshotted at creation. */
  createdByPicture?: string | null;

  /** Duration of the track's default version, in milliseconds. Denormalized
   *  from the default version file's `metadata.duration`. */
  durationMs?: number;

  /**
   * @deprecated Removed by the shared-track permissions design
   * (2026-09-08). No rule and no callable reads it.
   * `migrate/2026-09/clearSharedOutField.js` deletes it from every document;
   * delete this line after that run.
   */
  sharedOut?: boolean;
  /**
   * increment(1) when the DEPTH identity set changes: versionFilesV2,
   * pinnedVersionFiles, defaultVersionId, files, artworkFile. Does NOT move
   * for comment summaries, readableByV2, updatedAt, title or description.
   * Written by the header projector (track-sharing plan §4.3,
   * `reprojectTrackHeaderIfChanged`); mirrored onto every `space-tracks-v1`
   * row's `contentRev`.
   */
  contentRev?: number;
  /**
   * Digest of the mirrored header. Diff guard for the header projector's
   * fan-out (track-sharing plan §4.3, `computeHeaderRev`). Never
   * client-read.
   */
  headerRev?: string;
  /**
   * A propagated mirror of the `membersRev` of the space that owns this
   * track, as it was at the last write to this field. The writer copies
   * the value and never increments it. An increment is not idempotent
   * (one delivery and a repeat delivery give different results) when the
   * task system re-delivers a task. Also, nothing can compare an
   * incremented value against its source for equality.
   *
   * Its only job is to change this document, so that open track listeners
   * re-run the security rules. Firestore re-runs a listener's rules when
   * the listened document changes, not when a document those rules `get()`
   * changes. The write itself is the mechanism; no rule and no client
   * reads the value.
   *
   * The field is absent until the first revocation touches the document;
   * that is safe.
   *
   * The field is exempt from indexes (`"indexes": []`). It changes on
   * every revocation, so an auto-index rewrites one index entry for each
   * content document on the exact write path this work exists to make
   * cheap.
   *
   * It does nothing for the media bytes: it re-runs rules, and rules do
   * not govern the bytes. They never did.
   */
  aclRev?: number;
};

/**
 * Denormalized per-space comment summary carried on a `space-tracks-v1` row
 * (track-sharing plan §2). Same shape as the `tracks-v2` summary fields
 * above, but keyed by `(spaceId, trackId)` instead of `trackId` alone —
 * computation is owned by the comment-summary re-keying work (plan §4.4),
 * not by `setTrackSpace()`. `setTrackSpace()` only ever writes the
 * zero/empty state (new rows have no comments yet) or carries an existing
 * row's summary forward unchanged across a Move.
 */
export type SpaceTrackRowSummary = {
  commentCount: number;
  featuredComment: TrackFeaturedComment | null;
  recentCommenters: TrackCommenterAvatar[];
  uniqueCommenterCount: number;
};

/**
 * A row in the top-level `space-tracks-v1/{spaceId}_{trackId}` join
 * collection (track-sharing plan §1.2/§2). Every track has exactly one
 * "origin" row (`spaceId === originSpaceId`) today; sharing (multiple rows
 * per track) ships in a later phase. The row is client-write-denied
 * (`firestore.rules`) — `setTrackSpace()` (`core/db/spaceTracks.ts`) is the
 * only writer.
 *
 * Deliberately NOT modeled as `Entity & SpaceTrackRowCore`: `Entity`'s own
 * `createdAt` field (auto-stamped by `createEntity` to `Date.now()` at write
 * time) would collide with this type's `createdAt`, which is instead a
 * MIRRORED header field — the track's canonical, immutable upload time, and
 * must survive both `createEntity`'s stamp and a Move's delete+recreate.
 * `setTrackSpace()` writes every field explicitly rather than routing
 * through `createEntity`.
 */
export type SpaceTrackRow = {
  /** MUST equal the document id, `${spaceId}_${trackId}` (plan §1.2). */
  id: Id;
  /** READ BY THE RULE (scalar equality reflection). */
  spaceId: Id;
  trackId: Id;
  /** READ BY THE RULE (private-inbox conjunct). `isOrigin` is DERIVED as
   *  `spaceId === originSpaceId`, never stored. */
  originSpaceId: Id;
  /** The TRACK's author, NOT the sharer. READ BY THE RULE. */
  createdBy: Id;
  /** Whoever performed the add/share/move that produced this row. */
  addedBy: Id;
  /** Origin row: the ORIGINAL upload time. Must survive a Move. */
  addedAt: number;

  summary: SpaceTrackRowSummary;

  // PER-SPACE ALIASES — copied from the track ONCE when the row is created
  // (`projectTrackAliases`, core/db/spaceTracks.ts) and never written by the
  // header projector again. After creation each row owns its own copy. Two
  // writers change them: `updateTrack` (the ORIGIN row, in the same
  // transaction as the canonical edit) and UPDATE_SPACE_TRACK (any row).
  displayTitle: string;
  /** Optional only because rows written before this field existed lack it;
   *  read an absent value as `null`. */
  description?: string | null;
  artistName: string | null;
  /** A per-space artwork is a SPACE_ATTACHMENT of THIS row's space. The
   *  value copied at creation is the track's own artwork (a TRACK_ATTACHMENT
   *  of the track) and is shared by every row created from it. */
  artworkFileId: Id | null;

  // RENDER HEADER — mirrored, diff-guarded, FLAT in version count. Owned by
  // the header projector; re-projected onto every row on every change.
  /** Canonical upload time (≠ addedAt). Mirrored from `track.createdAt`. */
  createdAt: number;
  durationMs: number | null;
  /** |versionFilesV2| */
  versionCount: number;
  /** |files| */
  attachmentCount: number;
  /** Resolved via `getDefaultVersionId`; an id, not a playability verdict. */
  defaultVersionFileId: Id | null;

  // CONTENT MIRROR (shared-track permissions design, 2026-09-08, §5.2).
  // The ids a track page renders, so a borrowing space never reads
  // `tracks-v2`. Owned by the header projector, like the header above.
  // Optional only because rows written before these fields existed lack
  // them. A reader treats an absent value as "not mirrored yet" and falls
  // back to `defaultVersionFileId`.
  /** `track.versionFilesV2`, same order. */
  versionFileIds?: Id[];
  /** `track.pinnedVersionFiles`, same order. */
  pinnedVersionFileIds?: Id[];
  /** `track.files`, the attachments. */
  attachmentFileIds?: Id[];
  /** `track.fileUpdatedAt`. */
  fileUpdatedAt?: number | null;

  contentRev: number;
  /**
   * Firestore `updateTime` (ms) of the track write that produced this row's
   * header projection (track-sharing plan §4.3). Stale-write precondition
   * for the header projector's fan-out: a row write only lands when its
   * incoming `srcUpdatedAt` is >= the row's currently stored value, so a
   * retried or out-of-order-delivered OLDER projection can never overwrite
   * a NEWER one. Deliberately NOT `track.updatedAt` — some track writers
   * (e.g. `computeDefaultVersionDuration`) bypass `updateEntity` and never
   * bump it, which would make two genuinely different track versions
   * indistinguishable. Metadata only; never client-read. Absent on rows
   * created before this field existed (Task 3) — treated as older than any
   * real projection.
   */
  srcUpdatedAt?: number;
};

export type Track = Entity & TrackCore;

/**
 * One entry of `GET_TRACK`'s `sharedIn` response field (track-sharing plan
 * §4.6, Task 9) — the data path for "uploaded to X, also in Y and Z."
 * Computed server-side from `space-tracks-v1` rows
 * (`where('trackId','==',trackId)`, a free top-level Admin-SDK query with no
 * client-side equivalent — a client's own `spaceId` is always pinned, by
 * design), filtered to spaces the CALLER can read: a borrower must never
 * learn about a space they have no access to just because it also holds a
 * copy of a track they CAN see.
 */
export type TrackSharedInEntry = {
  spaceId: Id;
  spaceName: string;
  isOrigin: boolean;
};

/**
 * UPDATE_SPACE_TRACK's payload (`functions/handlers/tracks/updateSpaceTrack.ts`).
 * Exactly the four per-space alias fields, spelled with the TRACK's field
 * names so `TrackDetails.tsx` can submit the same shape to either callable.
 * `null` clears a value; an absent key leaves it alone.
 */
export type SpaceTrackAliasUpdate = {
  title?: string;
  description?: string | null;
  artistName?: string | null;
  artworkFile?: Id | null;
};

export type UpdateSpaceTrackPayload = {
  /** The VIEWING space — the row to edit. */
  spaceId: Id;
  trackId: Id;
  data: SpaceTrackAliasUpdate;
};

export type TrackPlayCore = {
  trackId: string;
  firstPlayedAt: number;
  lastPlayedAt: number;
  numPlays: number;
};

/*
 * TrackPlay represents an event when a user plays a track, which we send
 * a notification for
 */
export type TrackPlay = Entity & TrackPlayCore;

/* COMMENTS
---------------------------------- */

/**
 * Fixed File entity ID for the shared "voice comment" audio asset.
 *
 * Every AI-generated voice comment points at this same File via a
 * `FileBlockV2`. Avoids re-uploading per-comment when we don't yet
 * generate per-comment TTS — one sample clip stands in.
 *
 * Uploaded to PROD storage at:
 *   stock-assets/sample-voice-comment/sample-voice-comment.mp3
 *
 * The same ID is reused across envs — anywhere we need a voice block we
 * reference this constant; the local emulator / DEV / STAGING just need
 * the same upload run against them.
 */
export const SAMPLE_VOICE_COMMENT_FILE_ID = "sample-voice-comment";

export enum COMMENT_BLOCK_TYPE {
  TEXT = "text",
  FILE = "file-v2",
  POLL = "poll-v2",
  POLL_REPLY = "poll-reply",
  CUSTOM = "custom",
  STICKER = "sticker",
  REACTION = "reaction",
}

type TextBlock = {
  type: COMMENT_BLOCK_TYPE.TEXT;
  text: string;
};

export type FileBlockV2 = {
  type: COMMENT_BLOCK_TYPE.FILE;
  text: string;
  file: Id;
};

export type PollOption = TextBlock | FileBlockV2;

export type PollBlockV2 = {
  type: COMMENT_BLOCK_TYPE.POLL;
  text: string;
  options: PollOption[];
};

export type CustomBlock = {
  type: COMMENT_BLOCK_TYPE.CUSTOM;
  id: "heart-sticker";
};

export type StickerIds = "heart-sticker";

export type StickerBlock = {
  type: COMMENT_BLOCK_TYPE.STICKER;
  id?: StickerIds;
  imageUrl?: string;
};

export type PollReplyBlock = {
  type: COMMENT_BLOCK_TYPE.POLL_REPLY;
  option: number;
};

export type ReactionBlock = {
  type: COMMENT_BLOCK_TYPE.REACTION;
  /** Unique identifier for the reaction (e.g., 'hn-fire-v1'). */
  reactionId: string;
};

export type CommentBlock =
  | FileBlockV2
  | TextBlock
  | PollBlockV2
  | CustomBlock
  | StickerBlock
  | PollReplyBlock
  | ReactionBlock;

export enum SOCIAL_ASSET_STATUS {
  GENERATING = "GENERATING",
  COMPLETE = "COMPLETE",
}

export type SocialAssetOutput = {
  url: string;
  theme: string;
  format: "png" | "mp4";
};

export enum SOCIAL_ASSET_ERROR {
  PNG_UPLOAD_FAILED = "PNG_UPLOAD_FAILED",
  PNG_RENDER_FAILED = "PNG_RENDER_FAILED",
  VIDEO_ENCODE_FAILED = "VIDEO_ENCODE_FAILED",
  AUDIO_PREP_FAILED = "AUDIO_PREP_FAILED",
  WAVEFORM_EXTRACT_FAILED = "WAVEFORM_EXTRACT_FAILED",
  WAVEFORM_RENDER_FAILED = "WAVEFORM_RENDER_FAILED",
  GENERATION_TIMEOUT = "GENERATION_TIMEOUT",
}

export type SocialAssets = {
  status?: SOCIAL_ASSET_STATUS;
  error?: boolean | null;
  errorCode?: SOCIAL_ASSET_ERROR | null;
  output?: SocialAssetOutput[];
  generatedAt?: number;
  /** Timestamp when the last task was scheduled. Used to detect superseded tasks. */
  lastScheduledAt?: number;
};

export type CommentCore = {
  spaceId: Id;
  trackId: Id;
  trackVersionIds?: Id[];
  timestamp?: number;
  duration?: number;
  replies?: Id[];
  blocks: CommentBlock[];
  commentType?: COMMENT_BLOCK_TYPE;
  parentComment?: Id;
  isResolved?: boolean;
  socialAssets?: SocialAssets;
  /**
   * Denormalized reaction counts maintained by Cloud Function triggers.
   * Maps reactionId to count (e.g., { "hn-fire-v1": 5, "hn-cool-v1": 3 })
   */
  reactionCounts?: Record<string, number>;
};

export type Comment = Entity &
  CommentCore & {
    /* Post-creation data for indexing */
    parentComment?: Id;
    rootComment?: Id;
  };

/* USERS
---------------------------------- */
export type UserSubscriptions = {
  active: Stripe.Subscription | null;
  scheduled: Stripe.SubscriptionSchedule | null;
};

export enum AUTH_PREFIX {
  APPLE = "apple",
  EMAIL = "auth0",
  SMS = "sms",
  GOOGLE = "google-oauth2",
}

export const DUPE_EMAIL_ACCOUNT_ERROR = {
  [AUTH_PREFIX.APPLE]: EXISTING_AUTH_WITH_APPLE_ERR,
  [AUTH_PREFIX.GOOGLE]: EXISTING_AUTH_WITH_GOOGLE_ERR,
};

export enum AUTH_ACTION {
  LINK = "link",
  UNLINK = "unlink",
}

export type DropboxAuthTokens = {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  refresh_token: string;
  scope: string;
  uid: string;
  account_id: string;
};

// RevenueCatEntitlement is used to represent the entitlement
// https://www.revenuecat.com/docs/api-v1#tag/customers/operation/subscribers
export type RevenueCatEntitlement = {
  expires_date: string | null;
  grace_period_expires_date: string | null;
  product_identifier: string;
  // this is not officially documented in RevenueCat API, but this field
  // is populated only for subscriptions purchased via Google Play Store
  // and indicates base plan ID for the subscription, which also tells us the
  // subscription interval
  product_plan_identifier?: string;
  purchase_date: string;
};

export type UserCore = {
  requestedAccountDeletionAt?: number;
  isVerified?: boolean;
  name?: string;
  picture?: string;
  hasAcceptedTOS?: boolean;

  // Credentials
  firstName?: string;
  lastName?: string;
  email?: string;
  appleId?: string;
  googleId?: string;
  phone?: string;
  connection?: string;

  // Account Linking
  primaryAccountId?: Id;
  linkedAccountsV2?: { id: string; connection: string; identifier: string }[];
  isMergingWith?: string[];
  isUnmergingFrom?: string[];
  checkedDuplicates?: boolean;
  deletedAuth0?: boolean;

  // Subcription Details
  storageUsed?: number;
  spacesUsed?: number;
  activeSpacesUsed?: number;
  tracksUsed?: number;
  trackVersionsUsed?: number;
  /*
   * subscriptions field contain information on user's active and scheduled
   * subscriptions in Stripe and is not applicable to users who have paid
   * through Apple or Google Play
   */
  subscriptions?: UserSubscriptions;
  /*
   * revenueCatEntitlement contains information on user's current entitlement
   * from RevenueCat and is not applicable to users who have paid via Stripe
   */
  revenueCatEntitlement?: RevenueCatEntitlement;
  /*
   * useRevenueCatSandbox indicates whether to use Revenue Cat sandbox data or
   * prod data. If this is true, then the backend will only listen to events
   * and fetch data from Revenue Cat sandbox and not from prod.
   */
  useRevenueCatSandbox?: boolean;
  subscriptionTier?: SUBSCRIPTION_TIER;
  /*
   * subscriptionPlatform indicates where the subscription was purchased, which
   * can either be web (Stripe) or mobile (Apple or Google Play)
   */
  subscriptionPlatform?: SUBSCRIPTION_PLATFORM;
  /*
   * previousActiveSubscription indicates previous Stripe subscription and is
   * not applicable to users who have paid through Apple or Google Play
   */
  previousActiveSubscription?: Stripe.Subscription;
  stripeCustomerId?: string;
  starterSpaceGenerated?: boolean;
  hasIntroducedToPrivateInbox?: boolean;

  // Used for tracking only.
  firstStripeChargeId?: string;
  firstStripeChargeAmount?: number;
  firstLogIn?: boolean;
  completedWelcomeSurvey?: boolean;

  // From Auth0
  user_metadata?: UserMetadata;

  // Custom Notification Settings
  draftSpaceId?: string;
  mutedSpaces?: string[];
  mutedCollections?: string[];
  lockModePlayerOnboarded?: boolean;
  draftSpaceOnboarded?: boolean;

  // controlled feature callout status
  passwordProtectionCalloutDismissed?: boolean;
  downloadControlCalloutDismissed?: boolean;
  audioQualityLockCalloutDismissed?: boolean;
  homeBannerDismissed?: boolean;

  // dropbox integration
  dropboxLinkedFrom?: string;
  dropboxAccountId?: string;
  dropboxTokens?: DropboxAuthTokens;

  // typeform submission integration
  submissionToken?: string;

  // feature cohorts (synced from Intercom segments with "app_cohort_" prefix)
  cohorts?: Record<string, boolean>;

  // experimental features
  isMobileUser?: boolean;
  // this is synced with space's `experimentalPrivateInboxEnabled` field
  experimentalPrivateInboxEnabled?: boolean;

  pinnedSpaces?: string[];
  /** Maps spaceId to the timestamp (ms) when the user last read the chat. */
  chatLastReadAt?: Record<string, number>;
  /**
   * Stores the space details from where the user signed up via mobile beta prompt.
   * This allows the mobile app to redirect users back to the same space after
   * their first login
   */
  mobileHandoffSpace?: { spaceId: string; shareKey: string; trackId?: string };
};

export type UserEntity = UserCore & Entity;

export type UserDeletionRequestCore = {
  email?: string;
  name: string;
  deletedEntitiesPaths?: string[];
  deletedFilesPaths?: string[];
  deletedAt?: string[];
};

export type UserDeletionRequest = UserDeletionRequestCore & Entity;

export type UserMetadata = {
  HMACUserId?: string;
  fromMobileApp?: boolean;
  /**
   * Free-text description of how this user comments (tone, references,
   * quirks). Used by the AI comment generator (`getGeneratedComments`)
   * when this user is selected as a comment author — the persona is woven
   * into the prompt so generated content matches the user's voice. Set on
   * demo users created by the Space Generator script.
   */
  demoUserPersona?: string;
  /**
   * Marks a user that exists only as Firestore content (no live signup
   * flow): admin Space Generator-created users, the curated DEMO_USERS
   * roster, and the bulk-generated fans. Lives in `user_metadata` rather
   * than as a top-level field so it stays alongside the other Auth0
   * metadata that originally surfaced it. Query from server via
   * `where("user_metadata.isDemoUser", "==", true)` — Firestore supports
   * dot-notation against map fields.
   */
  isDemoUser?: boolean;
  /**
   * Subset of `isDemoUser` — flags an anonymous "fan" body that exists
   * purely for crowd-fill in generated spaces. Distinct from curated
   * persona users (which have `demoUserPersona` text and a SPACE_MEMBER /
   * SPACE_HOST role).
   */
  isDemoUserFan?: boolean;
} & Record<string, unknown>;

export type PublicUser = {
  id: string;
  isVerified?: boolean;
  name?: string;
  picture?: string;
  createdAt?: number; // timestamp in milliseconds
};

export type TypeformConfigDoc = {
  enabled: boolean;
  formId: string;
  spaceId: string; // Primary space ID (where approved tracks go)
  typeformAccessToken: string;
  webhookSecret: string;
  submissionsInboxSpaceId?: string; // Auto-created inbox where submissions land for review
  landingPageSlug?: string; // Framer landing page slug
  useSubmissionsInboxSpace?: boolean; // If true, submissions go to inbox first; defaults to false
};

export type TypeformConfigEntity = TypeformConfigDoc & EntityCore;

/* AD ENTITY
---------------------------------- */
export type AdCore = {
  imageUrl: string;
  // Legacy body copy. Kept for backward-compatibility so clients that have
  // not yet migrated to `body` can fall back to this field. New ads should
  // populate both `text` and `body` with the same value during the
  // transition window.
  text: string;
  linkUrl: string;
  advertiserName: string;
  advertiserAvatarUrl?: string;
  isActive: boolean;
  priority: number;
  adType: "sponsored" | "brandPartner" | "spacePinned";

  // Campaign grouping (free-text slug used as a reporting dimension).
  // No separate collection — filter by this at query/report time.
  campaignId?: string;

  // Creative copy — split out for future-proofing. `text` is the legacy
  // body field; `title` / `body` / `ctaText` let us ship richer layouts
  // without breaking older clients.
  title?: string;
  body?: string;
  ctaText?: string;

  // Scheduling
  startAt?: FirebaseFirestore.Timestamp;
  endAt?: FirebaseFirestore.Timestamp;

  // Targeting
  targetCohortKeys?: string[];
  targetUserIds?: string[];
  targetSpaceIds?: string[];

  // Future Kevel support
  impressionTrackingUrl?: string;
  clickTrackingUrl?: string;
};

export type AdEntity = AdCore & { id: string };

/* ADS CONFIG
---------------------------------- */
/**
 * Runtime-tunable configuration for the ads pipeline. Stored as a single
 * Firestore document at `config-v1/ads`. The doc may be missing or only
 * partially populated — the `getAds` handler merges with
 * `DEFAULT_ADS_CONFIG` below so any missing field falls back to a safe
 * default.
 */
export type AdsConfig = {
  /** Master kill switch for carousel ads. */
  carouselEnabled: boolean;
  /** Inject an ad every N comments. */
  carouselInterval: number;
  /** Require at least N comments before the first ad is injected. */
  carouselMinBefore: number;
  /** Master kill switch for pinned space-item ads. */
  spacePinnedEnabled: boolean;
  /** Maximum number of pinned ads returned per request. */
  spacePinnedMax: number;
};

export const DEFAULT_ADS_CONFIG: AdsConfig = {
  carouselEnabled: true,
  carouselInterval: 5,
  carouselMinBefore: 3,
  spacePinnedEnabled: true,
  spacePinnedMax: 2,
};

/* ALL COLLECTIONS
---------------------------------- */

export enum COLLECTION_ID {
  USER_DELETION_REQUEST = "user-deletion-request",
  DOWNLOAD_REQUEST = "download-request",
  DROPBOX_JOBS = "dropbox-jobs",
  TRACK = "tracks-v2",
  SPACE = "spaces-v1",
  COMMENT = "comments-v1",
  FILE = "files-v1",
  ADS = "ads-v1",
  CONFIG = "config-v1",
  USER = "users-v1",
  PUBLIC_USER = "public-users-v1",
  TRACK_PLAY = "track-plays-v1",
  TYPEFORM_CONFIGS = "space-typeform-configs",
  /**
   * Top-level join collection, `space-tracks-v1/{spaceId}_{trackId}`
   * (track-sharing plan §1.2). Client-write-denied — `setTrackSpace()`
   * (`core/db/spaceTracks.ts`) is the only writer.
   */
  SPACE_TRACK = "space-tracks-v1",
  /**
   * @deprecated
   * TODO: this is now deprecated & replaced by nested spaces and may be removed
   * https://www.notion.so/highnotefm/Remove-all-remaining-usage-of-collections-from-the-codebase-19315dd1482880d98a41c434cf1b319c
   */
  COLLECTION = "collections-v1",
}

export enum ARCHIVABLE_ENTITY_TYPES {
  SPACE = COLLECTION_ID.SPACE,
  TRACK = COLLECTION_ID.TRACK,
  FILE = COLLECTION_ID.FILE,
  TRACK_FILES = "trackFiles",
}

export enum OAUTH_PROVIDERS {
  DROPBOX = "dropbox",
}

const baseSelectionSchema = z.object({
  entityId: z.string(),
  entityType: z.enum([
    COLLECTION_ID.TRACK,
    COLLECTION_ID.FILE,
    COLLECTION_ID.SPACE,
  ]),
});

export const selectionSchema = baseSelectionSchema.extend({
  parentEntity: z
    .object({
      entityId: z.string().nullable(),
      entityType: z.enum([COLLECTION_ID.TRACK, COLLECTION_ID.SPACE]),
    })
    .optional(),
  /**
   * The space the caller is viewing this entity from. Only meaningful for
   * TRACK selections: it lets `deleteOrRemoveEntities` (the bulk
   * multi-select delete path) tell a hard-delete of the canonical Track
   * apart from a delete issued while standing in a Space that's only
   * BORROWING the track — which must go through UNSHARE_TRACK instead. See
   * the identical `spaceId` guard on the single-track `deleteTrack.ts` path.
   */
  spaceId: z.string().optional(),
});

export type SelectionType = z.infer<typeof selectionSchema>;

/* SUBSCRIPTIONS
---------------------------------- */
// WARNING!!!!
// If you edit this enum, you must edit the matching values in
// @highnote/server/storage.rules!!!!
export enum SUBSCRIPTION_TIER {
  FREE = "FREE",
  INDIE = "INDIE",
  /**
   * @deprecated
   * TODO: we deprecated PRO plan, and this may be removed
   * https://www.notion.so/highnotefm/Remove-all-remaining-usage-of-PRO-plan-from-the-codebase-19315dd14828805eaf87f08c12d4e095
   */
  PRO = "PRO",
  STUDIO = "STUDIO",
}

export enum SUBSCRIPTION_PLATFORM {
  /*
   * WEB is for subscriptions purchased through Stripe
   */
  WEB = "WEB",
  /*
   * MOBILE is for subscriptions purchased through Apple or Google Play
   */
  MOBILE = "MOBILE",
}

export enum MOBILE_SUBSCRIPTION_TIER {
  // keys are capitlized to match the values in Revenue Cat
  Indie = "Indie",
  Studio = "Studio",
  // Free tier does not exist as an entitlement in Revenue Cat. This is simply
  // used as a default value in the codebase.
  Free = "Free",
}

export const SUBSCRIPTION_TIER_FORMATTED: Record<SUBSCRIPTION_TIER, string> = {
  [SUBSCRIPTION_TIER.FREE]: "Free",
  [SUBSCRIPTION_TIER.INDIE]: "Indie",
  [SUBSCRIPTION_TIER.PRO]: "Pro",
  [SUBSCRIPTION_TIER.STUDIO]: "Studio",
};

export enum SUBSCRIPTION_INTERVAL {
  MONTHLY = "month",
  ANNUAL = "year",
}

export const STRIPE_INTERVAL_FORMATTED: Partial<
  Record<Stripe.Price.Recurring.Interval, string>
> = {
  [SUBSCRIPTION_INTERVAL.MONTHLY]: "Monthly",
  [SUBSCRIPTION_INTERVAL.ANNUAL]: "Annual",
};

export const useProductionPlans = process.env.DEPLOY_ENV === "production";

type SubscriptionPricing = Record<
  SUBSCRIPTION_TIER,
  Record<SUBSCRIPTION_INTERVAL, string | null>
>;

const SUBSCRIPTION_PRICING_DEV: SubscriptionPricing = {
  [SUBSCRIPTION_TIER.FREE]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: null,
    [SUBSCRIPTION_INTERVAL.ANNUAL]: null,
  },
  [SUBSCRIPTION_TIER.INDIE]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: "price_1PxxQZABNxgmdkGveiKwLFnl",
    [SUBSCRIPTION_INTERVAL.ANNUAL]: "price_1PxxQZABNxgmdkGvVaG6bjY0",
  },
  [SUBSCRIPTION_TIER.PRO]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: null,
    [SUBSCRIPTION_INTERVAL.ANNUAL]: null,
  },
  [SUBSCRIPTION_TIER.STUDIO]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: "price_1QS3HfABNxgmdkGvFqXzcIjp",
    [SUBSCRIPTION_INTERVAL.ANNUAL]: "price_1QS3IIABNxgmdkGvTPDnI8c9",
  },
};

const SUBSCRIPTION_PRICING_PROD: SubscriptionPricing = {
  [SUBSCRIPTION_TIER.FREE]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: null,
    [SUBSCRIPTION_INTERVAL.ANNUAL]: null,
  },
  [SUBSCRIPTION_TIER.INDIE]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: "price_1PwVhuABNxgmdkGvI5QQYQ9r",
    [SUBSCRIPTION_INTERVAL.ANNUAL]: "price_1PwVhuABNxgmdkGvFMisaWLY",
  },
  [SUBSCRIPTION_TIER.PRO]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: null,
    [SUBSCRIPTION_INTERVAL.ANNUAL]: null,
  },
  [SUBSCRIPTION_TIER.STUDIO]: {
    [SUBSCRIPTION_INTERVAL.MONTHLY]: "price_1PwVljABNxgmdkGvfju5EzYa",
    [SUBSCRIPTION_INTERVAL.ANNUAL]: "price_1QRPESABNxgmdkGvfGKeYOja",
  },
};

export const SUBSCRIPTION_PRICING: SubscriptionPricing = useProductionPlans
  ? SUBSCRIPTION_PRICING_PROD
  : SUBSCRIPTION_PRICING_DEV;

export const SUBSCRIPTION_PRICE_IDS = Object.keys(SUBSCRIPTION_PRICING).reduce(
  (acc, tier: SUBSCRIPTION_TIER) => {
    const intervalMap = SUBSCRIPTION_PRICING[tier];
    acc.push(...Object.values(intervalMap).filter(Boolean));
    return acc;
  },
  [],
);

export const SUBSCRIPTION_TIER_HIERARCHY = [
  SUBSCRIPTION_TIER.FREE,
  SUBSCRIPTION_TIER.INDIE,
  SUBSCRIPTION_TIER.PRO,
  SUBSCRIPTION_TIER.STUDIO,
];

export enum REVENUECAT_PRODUCT_PROVIDER {
  APPLE = "app_store",
  GOOGLE = "play_store",
}

// values must match what's in https://app.revenuecat.com/projects/1a709ad3/products
export const REVENUECAT_PRODUCT_ID = {
  IOS_INDIE_MONTHLY: "k3oeif",
  IOS_INDIE_ANNUAL: "7kzG6f",
  IOS_STUDIO_MONTHLY: "6dupbE",
  IOS_STUDIO_ANNUAL: "26Fovg",
  ANDROID_INDIE_MONTHLY: "indie-monthly",
  ANDROID_INDIE_ANNUAL: "indie-yearly",
  ANDROID_STUDIO_MONTHLY: "studio-monthly",
  ANDROID_STUDIO_ANNUAL: "studio-yearly",
};

export const REVENUECAT_PRODUCT_ID_TO_SUBSCRIPTION: {
  [K in (typeof REVENUECAT_PRODUCT_ID)[keyof typeof REVENUECAT_PRODUCT_ID]]: {
    provider: REVENUECAT_PRODUCT_PROVIDER;
    tier: SUBSCRIPTION_TIER;
    interval: SUBSCRIPTION_INTERVAL;
  };
} = {
  // iOS
  [REVENUECAT_PRODUCT_ID.IOS_INDIE_MONTHLY]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.APPLE,
    tier: SUBSCRIPTION_TIER.INDIE,
    interval: SUBSCRIPTION_INTERVAL.MONTHLY,
  },
  [REVENUECAT_PRODUCT_ID.IOS_INDIE_ANNUAL]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.APPLE,
    tier: SUBSCRIPTION_TIER.INDIE,
    interval: SUBSCRIPTION_INTERVAL.ANNUAL,
  },
  [REVENUECAT_PRODUCT_ID.IOS_STUDIO_MONTHLY]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.APPLE,
    tier: SUBSCRIPTION_TIER.STUDIO,
    interval: SUBSCRIPTION_INTERVAL.MONTHLY,
  },
  [REVENUECAT_PRODUCT_ID.IOS_STUDIO_ANNUAL]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.APPLE,
    tier: SUBSCRIPTION_TIER.STUDIO,
    interval: SUBSCRIPTION_INTERVAL.ANNUAL,
  },
  // Android
  [REVENUECAT_PRODUCT_ID.ANDROID_INDIE_MONTHLY]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.GOOGLE,
    tier: SUBSCRIPTION_TIER.INDIE,
    interval: SUBSCRIPTION_INTERVAL.MONTHLY,
  },
  [REVENUECAT_PRODUCT_ID.ANDROID_INDIE_ANNUAL]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.GOOGLE,
    tier: SUBSCRIPTION_TIER.INDIE,
    interval: SUBSCRIPTION_INTERVAL.ANNUAL,
  },
  [REVENUECAT_PRODUCT_ID.ANDROID_STUDIO_MONTHLY]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.GOOGLE,
    tier: SUBSCRIPTION_TIER.STUDIO,
    interval: SUBSCRIPTION_INTERVAL.MONTHLY,
  },
  [REVENUECAT_PRODUCT_ID.ANDROID_STUDIO_ANNUAL]: {
    provider: REVENUECAT_PRODUCT_PROVIDER.GOOGLE,
    tier: SUBSCRIPTION_TIER.STUDIO,
    interval: SUBSCRIPTION_INTERVAL.ANNUAL,
  },
};

/* SEGMENT IDS
---------------------------------- */
export enum EVENT_ID {
  CREATED_COMMENT = "Created Comment",
  UPDATED_COMMENT = "Updated Comment",
  REPLIED_TO_COMMENT = "Replied to Comment",
  DELETED_COMMENT = "Deleted Comment",

  TRCK_VERSIONS_USED_UPDATED = "trackVersionsUsed Updated",
  CREATED_TRACK = "Created Track",
  ADDED_TRACK_VERSION = "Updated Track: Added Version",
  REMOVED_TRACK_VERSION = "Updated Track: Removed Version",
  PLAYED_TRACK = "Played Track",

  CREATED_SPACE = "Created Space",
  ADDED_SPACE_MEMBER_NEW = "Updated Space: Invited New User",
  ADDED_SPACE_MEMBER_EXISTING = "Updated Space: Invited Existing User",
  ADDED_SPACE_TRACK = "Updated Space: Added Track",
  REMOVED_SPACE_TRACK = "Updated Space: Removed Track",
  DELETED_SPACE = "Deleted Space",
  ASSIGNED_SPACE_ROLE = "Updated Space: Assigned Role",
  UPDATED_SPACE_LINK = "Updated Space: Updated Link Permissions",

  UPDATED_USER_EMAIL = "Updated Profile: Updated Email",
  UPDATED_USER_NAME = "Updated Profile: Updated Name",
  SET_ANON_ALIAS = "Updated Profile: Set Anon Alias",
  SAW_ANON_PROMPT = "Viewed Dialog: Anon Warning",

  ENTERED_SPACE = "Entered Page: Space",
  EXITED_SPACE = "Exited Page: Space",
  ENTERED_TRACK = "Entered Page: Track",
  EXITED_TRACK = "Exited Page: Track",
  ENTERED_LIBRARY = "Entered Page: Library",
  EXITED_LIBRARY = "Exited Page: Library",
  ENTERED_ACCOUNT_SETTINGS = "Entered Page: Account Settings",
  EXITED_ACCOUNT_SETTINGS = "Exited Page: Account Settings",
  COMMENT_GEN_VIEWED = "Comment Gen: Viewed Comment Generator",
  COMMENT_GEN_VIEWED_ANON = "Comment Gen: Viewed Comment Generator Anon",
  COMMENT_GEN_SELECTED_PERSONA = "Comment Gen: Selected Persona",
  COMMENT_GEN_POPULATED_FILE = "Comment Gen: Populated File",
  COMMENT_GEN_REDIRECTING_TO_AUTH = "Comment Gen: Redirecting to Auth",
  COMMENT_GEN_RETURNED_FROM_AUTH = "Comment Gen: Returned from Auth Redirect",
  COMMENT_GEN_UPLOADED_TRACK = "Comment Gen: Uploaded Track",
  COMMENT_GEN_CREATED_ENTITIES = "Comment Gen: Created Entities",
  COMMENT_GEN_PROCESSED_AUDIO = "Comment Gen: Processed Audio",
  COMMENT_GEN_TRANSCRIBED_AUDIO = "Comment Gen: Transcribed Audio",
  COMMENT_GEN_GENERATED_COMMENTS = "Comment Gen: Generated Comments",
  COMMENT_GEN_REDIRECTING_TO_TRACK = "Comment Gen: Redirecting to Track Page",

  ENTERED_SIDEBAR_SPACE_SHARE = "Opened Sidebar: Share Space",
  EXITED_SIDEBAR_SPACE_SHARE = "Closed Sidebar: Share Space",

  UPLOADED_FILE = "Uploaded File",
  DELETED_FILE = "Deleted File",

  STRIPE_INVOICE_CREATED = "Invoice Created",
  SUBSCRIPTION_UPDATED = "Updated Subscription",
  SUBSCRIPTION_CANCELED = "Canceled Subscription",
  ABANDONED_CHECKOUT = "Abandoned Checkout",

  LOGGED_IN = "Logged In",
  SIGNED_UP = "Signed Up",
  ACCOUNT_DELETION_REQUESTED = "Account Deletion Requested",

  HELPER_ARCHIVE_TRACK = "Helper: Archive Track",

  CREATED_INVITE_LINK = "Invite Link Flow: Created Invite Link",
  COPIED_INVITE_LINK = "Invite Link Flow: Copied Invite Link",
  RECIPIENT_OPENED_LINK = "Invite Link Flow: Recipient Opened Link",
  RECIPIENT_OPENED_JOIN_DIALOG = "Invite Link Flow: Recipient Triggered Display Name Dialog",
  RECIPIENT_ADDED_NAME = "Invite Link Flow: Recipient Created Display Name",
  RECIPIENT_STAYED_ANON = "Invite Link Flow: Recipient Declined Display Name",
  RECIPIENT_LOGGED_IN = "Invite Link Flow: Recipient Logged In",
  RECIPIENT_SIGNED_UP = "Invite Link Flow: Recipient Registered",
  RECIPIENT_JOINED_ENTITY = "Invite Link Flow: Recipient Joined Entity",
  RECIPIENT_CLICKED_JOIN = "Invite Link Flow: Recipient Clicked Join Button",

  SENT_NOTIFICATION = "Sent Notification",
  RECIPIENT_OPENED_NOTIFICATION = "Recipient Opened Notification Link",

  TRACK_LIMIT_REACHED_VIEWED_PAYWALL = "Track Limit Reached: Viewed Paywall",
  TRACK_LIMIT_REACHED_CLICKED_UPGRADE = "Track Limit Reached: Clicked Upgrade",
  TRACK_LIMIT_REACHED_CLICKED_MANAGE_TRACKS = "Track Limit Reached: Clicked Manage Tracks",
  TRACK_LIMIT_REACHED_DISMISSED_PAYWALL = "Track Limit Reached: Dismissed Paywall",

  // Submission flow events
  GOT_SIGN_UP_FROM_SUBMIT_TRACK_CTA = "Submission: Got Sign Up From Submit Track CTA",
  SIGNED_UP_FROM_SUBMIT_TRACK_CTA = "Submission: Signed Up From Submit Track CTA",
  RECEIVED_TRACK_SUBMISSION = "Submission: Received Track Submission",
  SUBMITTED_TRACK = "Submission: Submitted Track",
  SUBMITTED_TRACK_ACCEPTED = "Submission: Submitted Track Accepted",

  // Ads surface events (server-authoritative; fired by TRACK_AD_EVENT)
  AD_IMPRESSION = "Ad Impression",
  AD_CLICK = "Ad Click",
}

export enum GROUP_ID {
  PAGES = "pages",
  DIALOGS = "dialogs",
  SIDEBARS = "sidebars",
  USER_PROFILE = "user_profile",
  USER_SUBSCRIPTION = "user_subscription",
  SPACES = "spaces",
  TRACKS = "tracks",
  COMMENTS = "comments",
  FILES = "files",
  HELPERS = "helpers",
  AUTHENTICATION = "authentication",
  INVITE_LINK_FLOW = "invite_link_flow",
  NOTIFICATION_FLOW = "notification_flow",
  COMMENT_GENERATOR = "comment_generator",
  FORM_SUBMISSION = "form_submission",
  ADS = "ads",
}

export const GROUP_BY_EVENT: Record<EVENT_ID, GROUP_ID> = {
  [EVENT_ID.CREATED_COMMENT]: GROUP_ID.COMMENTS,
  [EVENT_ID.UPDATED_COMMENT]: GROUP_ID.COMMENTS,
  [EVENT_ID.REPLIED_TO_COMMENT]: GROUP_ID.COMMENTS,
  [EVENT_ID.DELETED_COMMENT]: GROUP_ID.COMMENTS,

  [EVENT_ID.CREATED_TRACK]: GROUP_ID.TRACKS,
  [EVENT_ID.ADDED_TRACK_VERSION]: GROUP_ID.TRACKS,
  [EVENT_ID.REMOVED_TRACK_VERSION]: GROUP_ID.TRACKS,
  [EVENT_ID.PLAYED_TRACK]: GROUP_ID.TRACKS,

  [EVENT_ID.CREATED_SPACE]: GROUP_ID.SPACES,
  [EVENT_ID.ADDED_SPACE_MEMBER_NEW]: GROUP_ID.SPACES,
  [EVENT_ID.ADDED_SPACE_MEMBER_EXISTING]: GROUP_ID.SPACES,
  [EVENT_ID.ADDED_SPACE_TRACK]: GROUP_ID.SPACES,
  [EVENT_ID.REMOVED_SPACE_TRACK]: GROUP_ID.SPACES,
  [EVENT_ID.DELETED_SPACE]: GROUP_ID.SPACES,
  [EVENT_ID.ASSIGNED_SPACE_ROLE]: GROUP_ID.SPACES,
  [EVENT_ID.UPDATED_SPACE_LINK]: GROUP_ID.SPACES,

  [EVENT_ID.UPDATED_USER_EMAIL]: GROUP_ID.USER_PROFILE,
  [EVENT_ID.UPDATED_USER_NAME]: GROUP_ID.USER_PROFILE,
  [EVENT_ID.SET_ANON_ALIAS]: GROUP_ID.USER_PROFILE,

  [EVENT_ID.SAW_ANON_PROMPT]: GROUP_ID.DIALOGS,

  [EVENT_ID.ENTERED_SPACE]: GROUP_ID.PAGES,
  [EVENT_ID.EXITED_SPACE]: GROUP_ID.PAGES,
  [EVENT_ID.ENTERED_TRACK]: GROUP_ID.PAGES,
  [EVENT_ID.EXITED_TRACK]: GROUP_ID.PAGES,
  [EVENT_ID.ENTERED_LIBRARY]: GROUP_ID.PAGES,
  [EVENT_ID.EXITED_LIBRARY]: GROUP_ID.PAGES,
  [EVENT_ID.ENTERED_ACCOUNT_SETTINGS]: GROUP_ID.PAGES,
  [EVENT_ID.EXITED_ACCOUNT_SETTINGS]: GROUP_ID.PAGES,

  [EVENT_ID.COMMENT_GEN_VIEWED]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_VIEWED_ANON]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_SELECTED_PERSONA]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_POPULATED_FILE]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_REDIRECTING_TO_AUTH]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_RETURNED_FROM_AUTH]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_UPLOADED_TRACK]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_CREATED_ENTITIES]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_PROCESSED_AUDIO]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_TRANSCRIBED_AUDIO]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_GENERATED_COMMENTS]: GROUP_ID.COMMENT_GENERATOR,
  [EVENT_ID.COMMENT_GEN_REDIRECTING_TO_TRACK]: GROUP_ID.COMMENT_GENERATOR,

  [EVENT_ID.ENTERED_SIDEBAR_SPACE_SHARE]: GROUP_ID.SIDEBARS,
  [EVENT_ID.EXITED_SIDEBAR_SPACE_SHARE]: GROUP_ID.SIDEBARS,

  [EVENT_ID.UPLOADED_FILE]: GROUP_ID.FILES,
  [EVENT_ID.DELETED_FILE]: GROUP_ID.FILES,

  [EVENT_ID.STRIPE_INVOICE_CREATED]: GROUP_ID.USER_SUBSCRIPTION,
  [EVENT_ID.SUBSCRIPTION_UPDATED]: GROUP_ID.USER_SUBSCRIPTION,
  [EVENT_ID.SUBSCRIPTION_CANCELED]: GROUP_ID.USER_SUBSCRIPTION,
  [EVENT_ID.ABANDONED_CHECKOUT]: GROUP_ID.USER_SUBSCRIPTION,

  [EVENT_ID.LOGGED_IN]: GROUP_ID.AUTHENTICATION,
  [EVENT_ID.SIGNED_UP]: GROUP_ID.AUTHENTICATION,
  [EVENT_ID.ACCOUNT_DELETION_REQUESTED]: GROUP_ID.AUTHENTICATION,

  [EVENT_ID.HELPER_ARCHIVE_TRACK]: GROUP_ID.HELPERS,

  [EVENT_ID.CREATED_INVITE_LINK]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.COPIED_INVITE_LINK]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_OPENED_LINK]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_OPENED_JOIN_DIALOG]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_ADDED_NAME]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_STAYED_ANON]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_LOGGED_IN]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_SIGNED_UP]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_JOINED_ENTITY]: GROUP_ID.INVITE_LINK_FLOW,
  [EVENT_ID.RECIPIENT_CLICKED_JOIN]: GROUP_ID.INVITE_LINK_FLOW,

  [EVENT_ID.SENT_NOTIFICATION]: GROUP_ID.NOTIFICATION_FLOW,
  [EVENT_ID.RECIPIENT_OPENED_NOTIFICATION]: GROUP_ID.NOTIFICATION_FLOW,

  [EVENT_ID.TRCK_VERSIONS_USED_UPDATED]: GROUP_ID.TRACKS,

  [EVENT_ID.TRACK_LIMIT_REACHED_VIEWED_PAYWALL]: GROUP_ID.TRACKS,
  [EVENT_ID.TRACK_LIMIT_REACHED_CLICKED_UPGRADE]: GROUP_ID.TRACKS,
  [EVENT_ID.TRACK_LIMIT_REACHED_CLICKED_MANAGE_TRACKS]: GROUP_ID.TRACKS,
  [EVENT_ID.TRACK_LIMIT_REACHED_DISMISSED_PAYWALL]: GROUP_ID.TRACKS,

  [EVENT_ID.GOT_SIGN_UP_FROM_SUBMIT_TRACK_CTA]: GROUP_ID.FORM_SUBMISSION,
  [EVENT_ID.SIGNED_UP_FROM_SUBMIT_TRACK_CTA]: GROUP_ID.FORM_SUBMISSION,
  [EVENT_ID.RECEIVED_TRACK_SUBMISSION]: GROUP_ID.FORM_SUBMISSION,
  [EVENT_ID.SUBMITTED_TRACK]: GROUP_ID.FORM_SUBMISSION,
  [EVENT_ID.SUBMITTED_TRACK_ACCEPTED]: GROUP_ID.FORM_SUBMISSION,

  [EVENT_ID.AD_IMPRESSION]: GROUP_ID.ADS,
  [EVENT_ID.AD_CLICK]: GROUP_ID.ADS,
};
